import { useEffect, useState } from 'react'

import { t } from '@molecule/app-i18n'
import { getClassMap } from '@molecule/app-ui'

import { caseKey, finalCases, type NormalizedTestRecord } from '../../../api/src/ai/test-record.js'
import {
  controlProjectTestRecord,
  type ProjectTestRecordPayload,
  watchProjectTestRecords,
} from '../lib/projectTestRecords.js'

export interface ProjectTestRecordsState {
  payload: ProjectTestRecordPayload | null
  unavailable: string | null
}
export function useProjectTestRecords(
  projectId: string,
  getAuthToken: () => string | null | undefined,
  enabled = true,
): ProjectTestRecordsState {
  const [state, setState] = useState<ProjectTestRecordsState>({ payload: null, unavailable: null })
  useEffect(() => {
    setState({ payload: null, unavailable: null })
    if (!enabled || !projectId) return
    return watchProjectTestRecords({
      projectId,
      getAuthToken,
      onSnapshot: (payload) => setState({ payload, unavailable: null }),
      onUnavailable: (unavailable) => setState((previous) => ({ ...previous, unavailable })),
    })
  }, [projectId, getAuthToken, enabled])
  return state
}
const label = (value: string) =>
  t(`ide.testRecords.status.${value}`, undefined, { defaultValue: value })
export function ProjectTestRecordStatus({
  state,
  onOpen,
}: {
  state: ProjectTestRecordsState
  onOpen: () => void
}) {
  const cm = getClassMap(),
    rows = state.payload?.snapshot.records.filter((r) => r.start.recordKind === 'invocation') || [],
    live = rows.filter((r) => r.state === 'live' && !r.finish).length
  return (
    <button
      type="button"
      className={cm.cn(cm.button({ variant: 'ghost', size: 'sm' }), cm.w('full'))}
      onClick={onOpen}
      data-mol-id="native-test-status"
    >
      {live
        ? t(
            'ide.testRecords.running',
            { count: live },
            { defaultValue: '{{count}} test invocations running' },
          )
        : t(
            'ide.testRecords.history',
            { count: rows.length },
            { defaultValue: 'Tests · {{count}} invocations' },
          )}
      {state.unavailable
        ? ` · ${t('ide.testRecords.unavailable', undefined, { defaultValue: 'status unavailable' })}`
        : ''}
    </button>
  )
}
function Cases({ record }: { record: NormalizedTestRecord }) {
  const rows = new Map<
    string,
    {
      title: string
      attempts: Map<string, string>
      final: string | null
      failure: string | undefined
    }
  >()
  for (const event of record.events)
    if (event.type === 'inventory')
      for (const entry of event.entries)
        rows.set(caseKey(entry.identity), {
          title: entry.identity.titlePath.join(' › '),
          attempts: new Map(),
          final: entry.state === 'selected' ? null : entry.state,
          failure: undefined,
        })
  const finals = finalCases(record)
  for (const event of record.events) {
    if (event.type !== 'case') continue
    const key = caseKey(event.identity),
      row = rows.get(key) || {
        title: event.identity.titlePath.join(' › '),
        attempts: new Map<string, string>(),
        final: null,
        failure: undefined,
      }
    if (event.phase === 'attempt' && event.attempt)
      row.attempts.set(
        event.attempt.nativeAttemptId,
        `${event.attempt.ordinal + 1}: ${label(event.attempt.outcome)}`,
      )
    else {
      const final = finals.get(key)
      row.final = final?.finalOutcome || 'unknown'
      row.failure = final?.failureOutput
    }
    rows.set(key, row)
  }
  return (
    <ul>
      {[...rows].map(([key, row]) => (
        <li key={key} data-mol-id="native-test-case">
          <span>{row.title}</span>
          {' · '}
          {row.final ? <strong>{label(row.final)}</strong> : label('pending')}
          {row.failure && <pre role="alert">{row.failure}</pre>}
          {row.attempts.size > 0 && (
            <div>
              {t('ide.testRecords.attempts', undefined, { defaultValue: 'Attempts' })}:{' '}
              {[...row.attempts.values()].join(', ')}
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}
function Cycle({ record }: { record: NormalizedTestRecord }) {
  const errors = record.events.filter((e) => e.type === 'runError'),
    results = record.events.some((e) => e.type === 'case' || e.type === 'result')
  return (
    <div data-mol-id="native-test-cycle">
      {!results && (
        <p>
          {t('ide.testRecords.notRecorded', undefined, {
            defaultValue: 'test results not recorded',
          })}
        </p>
      )}
      <Cases record={record} />
      {errors.map((e) =>
        e.type === 'runError' ? (
          <pre role="alert" key={e.errorId}>
            {e.error}
          </pre>
        ) : null,
      )}
      {record.finish?.suiteError === 'unresolved' && (
        <p role="status">
          {t('ide.testRecords.suiteError', undefined, {
            defaultValue:
              'Run error. Passing results remain visible; deployed proof is incomplete.',
          })}
        </p>
      )}
    </div>
  )
}
export function ProjectTestRecordHistory({
  state,
  projectId,
  getAuthToken,
  canControl,
}: {
  state: ProjectTestRecordsState
  projectId: string
  getAuthToken: () => string | null | undefined
  canControl: boolean
}) {
  const cm = getClassMap(),
    [pending, setPending] = useState<Set<string>>(new Set()),
    [controlMessage, setControlMessage] = useState<string | null>(null)
  const snapshot = state.payload?.snapshot,
    invocations = snapshot?.records.filter((r) => r.start.recordKind === 'invocation') || []
  const control = async (record: NormalizedTestRecord, action: 'stop' | 'skip') => {
    if (!canControl || !record.start.plan) return
    setPending((p) => new Set(p).add(record.start.runId))
    setControlMessage(null)
    try {
      const result = await controlProjectTestRecord({
        projectId,
        runId: record.start.runId,
        plan: record.start.plan,
        action,
        requestId: crypto.randomUUID(),
        authToken: getAuthToken(),
      })
      if (!result.accepted)
        setControlMessage(
          t(
            'ide.testRecords.controlRefused',
            { state: result.state },
            { defaultValue: 'Control unavailable: {{state}}' },
          ),
        )
    } catch (_error) {
      setControlMessage(
        t('ide.testRecords.controlFailed', undefined, {
          defaultValue: 'The test control could not be delivered.',
        }),
      )
    } finally {
      setPending((p) => {
        const next = new Set(p)
        next.delete(record.start.runId)
        return next
      })
    }
  }
  return (
    <section
      aria-label={t('ide.testRecords.title', undefined, { defaultValue: 'Native test runs' })}
      data-mol-id="native-test-history"
      data-watermark={snapshot ? JSON.stringify(snapshot.watermark) : ''}
      className={cm.card()}
    >
      <h3>{t('ide.testRecords.title', undefined, { defaultValue: 'Native test runs' })}</h3>
      {state.unavailable && (
        <p role="status">
          {t('ide.testRecords.connectionUnavailable', undefined, {
            defaultValue:
              'Test records are temporarily unavailable. Retained results are shown below.',
          })}
        </p>
      )}
      {controlMessage && <p role="alert">{controlMessage}</p>}
      {!invocations.length && (
        <p>
          {t('ide.testRecords.empty', undefined, {
            defaultValue: 'No recorded test invocations yet.',
          })}
        </p>
      )}
      {invocations.map((invocation) => {
        const id = invocation.start.runId,
          cycles = snapshot!.records.filter(
            (r) => r.start.recordKind === 'cycle' && r.start.launcherSessionId === id,
          ),
          finish = invocation.finish,
          live = invocation.state === 'live' && !finish
        const hasResults = cycles.some((r) =>
          r.events.some((e) => e.type === 'case' || e.type === 'result'),
        )
        const exit = finish?.runnerExit
        const status = finish?.timeout
          ? label('timeout')
          : exit?.state === 'known'
            ? exit.signal
              ? t(
                  'ide.testRecords.signal',
                  { signal: exit.signal },
                  { defaultValue: 'signal {{signal}}' },
                )
              : t(
                  'ide.testRecords.exit',
                  { code: exit.code ?? 'unknown' },
                  { defaultValue: 'exit {{code}}' },
                )
            : label(finish?.outcome || invocation.state)
        return (
          <details key={id} open={live} data-mol-id="native-test-invocation">
            <summary>
              {invocation.start.runner}: {invocation.start.invocation.argv.join(' ')} · {status}
              {finish?.outcome === 'skipped' ? ` · ${label('skipped')}` : ''}
              {invocation.events.some((e) => e.type === 'runError') ||
              cycles.some((r) => r.finish?.suiteError === 'unresolved')
                ? ` · ${t('ide.testRecords.runError', undefined, { defaultValue: 'Run error' })}`
                : ''}
            </summary>
            {finish?.timeout && (
              <p>
                {t(
                  finish.timeout.scope === 'command'
                    ? 'ide.testRecords.commandTimeout'
                    : 'ide.testRecords.runTimeout',
                  { seconds: Math.round(finish.timeout.ms / 1000) },
                  {
                    defaultValue:
                      finish.timeout.scope === 'command'
                        ? 'A command passed its {{seconds}}s limit and the run was stopped.'
                        : 'The run passed its {{seconds}}s budget and was stopped.',
                  },
                )}
              </p>
            )}
            {!hasResults && (
              <p>
                {t('ide.testRecords.notRecorded', undefined, {
                  defaultValue: 'test results not recorded',
                })}
              </p>
            )}
            {(invocation.start.runner === 'hurl' ||
              cycles.some((c) => c.start.runner === 'hurl')) &&
              !hasResults && (
                <p>
                  {t('ide.testRecords.hurlPending', undefined, {
                    defaultValue:
                      'Per-file results are available only after Hurl exits with a complete private report.',
                  })}
                </p>
              )}
            {(invocation.start.runner === 'hurl' ||
              cycles.some((c) => c.start.runner === 'hurl')) && (
              <p>
                {t('ide.testRecords.hurlDiagnostic', undefined, {
                  defaultValue:
                    'Recording diagnostic: Hurl may print “Writing JSON report to …” in verbose/debug output.',
                })}
              </p>
            )}
            {invocation.events.map((e) =>
              e.type === 'runError' ? (
                <pre role="alert" key={e.errorId}>
                  {e.error}
                </pre>
              ) : null,
            )}
            {live && invocation.start.plan && (
              <div>
                {(['stop', 'skip'] as const).map((action) => (
                  <button
                    key={action}
                    type="button"
                    className={cm.button({ variant: 'outline', size: 'sm' })}
                    disabled={
                      !canControl ||
                      pending.has(id) ||
                      invocation.events.some((e) => e.type === 'control')
                    }
                    onClick={() => void control(invocation, action)}
                  >
                    {action === 'stop'
                      ? t('ide.testRecords.stop', undefined, { defaultValue: 'Stop' })
                      : t('ide.testRecords.skip', undefined, { defaultValue: 'Skip' })}
                  </button>
                ))}
              </div>
            )}
            {cycles.length === 1 ? (
              <Cycle record={cycles[0]!} />
            ) : (
              cycles.map((cycle) => (
                <details key={cycle.start.runId} open={cycle.state === 'live'}>
                  <summary>
                    {t(
                      'ide.testRecords.cycle',
                      { ordinal: cycle.start.cycleOrdinal },
                      { defaultValue: 'Cycle {{ordinal}}' },
                    )}{' '}
                    · {label(cycle.finish?.outcome || cycle.state)}
                  </summary>
                  <Cycle record={cycle} />
                </details>
              ))
            )}
            <pre
              style={{ maxHeight: '24rem', overflow: 'auto', whiteSpace: 'pre-wrap' }}
              aria-label={t('ide.testRecords.output', undefined, { defaultValue: 'Native output' })}
            >
              {invocation.events.flatMap((e) => (e.type === 'output' ? [e.chunk] : [])).join('')}
            </pre>
          </details>
        )
      })}
      {state.payload && (
        <p
          data-mol-id="native-test-proof"
          data-watermark={JSON.stringify(state.payload.proof.watermark)}
        >
          {state.unavailable
            ? t('ide.testRecords.proofUnavailable', undefined, {
                defaultValue: 'Deployed proof unavailable until test records can be read.',
              })
            : !state.payload.proof.inventoryRevisions.length
              ? t('ide.testRecords.noProofObligation', undefined, {
                  defaultValue: 'No supplied deployed test suite is registered.',
                })
              : state.payload.proof.verdict.complete
                ? t('ide.testRecords.proofComplete', undefined, {
                    defaultValue: 'Deployed proof complete for the applicable supplied suites.',
                  })
                : t('ide.testRecords.proofIncomplete', undefined, {
                    defaultValue: 'Deployed proof incomplete.',
                  })}
        </p>
      )}
    </section>
  )
}
